import { useCallback, useEffect, useRef, useState } from "react";
import { loadPreferences, savePreferences } from "../../lib/preferences";
import type { NetworkQuality, TalkPreferences } from "../../lib/types";

export interface MediaDeviceLists {
  cameras: MediaDeviceInfo[];
  microphones: MediaDeviceInfo[];
  speakers: MediaDeviceInfo[];
}

const emptyDevices: MediaDeviceLists = { cameras: [], microphones: [], speakers: [] };

function networkQuality(): NetworkQuality {
  if (!navigator.onLine) return "offline";
  const connection = (navigator as Navigator & { connection?: { effectiveType?: string; downlink?: number } }).connection;
  if (connection?.effectiveType === "2g" || connection?.effectiveType === "slow-2g" || (connection?.downlink !== undefined && connection.downlink < 1.2)) return "limited";
  return "good";
}

export function useDevicePreview(videoRef: React.RefObject<HTMLVideoElement | null>) {
  const [preferences, setPreferences] = useState<TalkPreferences>(() => loadPreferences());
  const [devices, setDevices] = useState<MediaDeviceLists>(emptyDevices);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [requesting, setRequesting] = useState(false);
  const [permissionMessage, setPermissionMessage] = useState("");
  const [network, setNetwork] = useState<NetworkQuality>(() => networkQuality());
  const streamRef = useRef<MediaStream | null>(null);

  const enumerate = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    const available = await navigator.mediaDevices.enumerateDevices();
    setDevices({
      cameras: available.filter((item) => item.kind === "videoinput"),
      microphones: available.filter((item) => item.kind === "audioinput"),
      speakers: available.filter((item) => item.kind === "audiooutput"),
    });
  }, []);

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setStream(null);
  }, []);

  const request = useCallback(async (nextPreferences = preferences) => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setPermissionMessage("This browser cannot preview camera or microphone. You can still join listen-only.");
      return;
    }
    setRequesting(true);
    setPermissionMessage("");
    streamRef.current?.getTracks().forEach((track) => track.stop());
    try {
      const nextStream = await navigator.mediaDevices.getUserMedia({
        video: nextPreferences.cameraEnabled ? {
          ...(nextPreferences.cameraDeviceId ? { deviceId: { exact: nextPreferences.cameraDeviceId } } : {}),
          width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30, max: 30 },
        } : false,
        audio: nextPreferences.microphoneEnabled ? {
          ...(nextPreferences.microphoneDeviceId ? { deviceId: { exact: nextPreferences.microphoneDeviceId } } : {}),
          echoCancellation: true, noiseSuppression: true, autoGainControl: true,
        } : false,
      });
      streamRef.current = nextStream;
      setStream(nextStream);
      await enumerate();
    } catch (error) {
      streamRef.current = null;
      setStream(null);
      const denied = error instanceof DOMException && (error.name === "NotAllowedError" || error.name === "SecurityError");
      setPermissionMessage(denied
        ? "Camera or microphone permission was denied. Turn both off to join with an avatar, or update browser permissions and try again."
        : "Talk could not start the selected devices. Choose another device or join with them off.");
    } finally {
      setRequesting(false);
    }
  }, [enumerate, preferences]);

  const update = useCallback((patch: Partial<TalkPreferences>, restart = false) => {
    setPreferences((current) => {
      const next = { ...current, ...patch };
      savePreferences(next);
      if (restart && streamRef.current) void request(next);
      else {
        if (patch.cameraEnabled !== undefined) streamRef.current?.getVideoTracks().forEach((track) => { track.enabled = patch.cameraEnabled ?? false; });
        if (patch.microphoneEnabled !== undefined) streamRef.current?.getAudioTracks().forEach((track) => { track.enabled = patch.microphoneEnabled ?? false; });
      }
      return next;
    });
  }, [request]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      if (stream) void videoRef.current.play().catch(() => undefined);
    }
  }, [stream, videoRef]);

  useEffect(() => {
    const mediaDevices = navigator.mediaDevices;
    if (mediaDevices?.enumerateDevices) {
      void mediaDevices.enumerateDevices().then((available) => setDevices({
        cameras: available.filter((item) => item.kind === "videoinput"),
        microphones: available.filter((item) => item.kind === "audioinput"),
        speakers: available.filter((item) => item.kind === "audiooutput"),
      })).catch(() => undefined);
    }
    const handleDevices = () => { void enumerate(); };
    mediaDevices?.addEventListener("devicechange", handleDevices);
    return () => mediaDevices?.removeEventListener("devicechange", handleDevices);
  }, [enumerate]);

  useEffect(() => {
    const updateNetwork = () => setNetwork(networkQuality());
    window.addEventListener("online", updateNetwork);
    window.addEventListener("offline", updateNetwork);
    const connection = (navigator as Navigator & { connection?: EventTarget }).connection;
    connection?.addEventListener("change", updateNetwork);
    return () => {
      window.removeEventListener("online", updateNetwork);
      window.removeEventListener("offline", updateNetwork);
      connection?.removeEventListener("change", updateNetwork);
    };
  }, []);

  useEffect(() => stop, [stop]);

  return { preferences, devices, stream, requesting, permissionMessage, network, request, update };
}
